import React from "react";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { NavLink, useLocation } from "react-router-dom";

import "./styles.css";

export interface AppSidebarSubItem {
  route: string;
  label: string;
  disabled?: boolean;
  action?: "logout";
}

export interface AppSidebarItem {
  icon: React.ReactNode;
  route: string;
  label: string;
  end?: boolean;
  /** Fica so no drawer mobile; no desktop o item vive no menu do header. */
  mobileOnly?: boolean;
  children?: AppSidebarSubItem[];
}

interface AppSidebarProps {
  items: AppSidebarItem[];
  logo: string;
  isOpen?: boolean;
  isLightMode?: boolean;
  isMobileOpen?: boolean;
  searchValue?: string;
  showAdminAccess?: boolean;
  onAdminAccessClick?: () => void;
  onMobileClose?: () => void;
  onNavigate?: () => void;
  onRequestOpen?: () => void;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: () => void;
  onThemeToggle?: () => void;
  onLogout?: () => void;
}

interface AppSidebarLocationState {
  from?: string;
}

const AppSidebar = ({
  items,
  logo,
  isOpen = true,
  isLightMode = true,
  isMobileOpen = false,
  searchValue = "",
  showAdminAccess = false,
  onAdminAccessClick,
  onMobileClose,
  onNavigate,
  onRequestOpen,
  onSearchChange,
  onSearchSubmit,
  onThemeToggle,
  onLogout,
}: AppSidebarProps) => {
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const location = useLocation();
  const locationState = location.state as AppSidebarLocationState | null;
  const isContentDetailsPage = location.pathname.startsWith(
    "/app/conteudo/detalhes/"
  );
  const detailsContentType = isContentDetailsPage
    ? location.pathname.split("/")[4]
    : undefined;
  const detailsContentRoute = detailsContentType
    ? `/app/conteudo/${detailsContentType}`
    : undefined;
  const detailsSubmenuRoute = items
    .flatMap((item) => item.children ?? [])
    .find((child) => child.route === detailsContentRoute)?.route;
  const originPath = locationState?.from;
  const cameFromOutsideContent = Boolean(
    originPath &&
      originPath !== "/app" &&
      !originPath.startsWith("/app/conteudo/")
  );
  const legacyDetailsOrigin =
    originPath && !originPath.startsWith("/app/conteudo/detalhes/")
      ? originPath
      : "/app/conteudo/cursos";
  const activePath = isContentDetailsPage
    ? cameFromOutsideContent
      ? (originPath as string)
      : detailsSubmenuRoute ??
        (detailsContentType === "conteudos" ? "/app" : legacyDetailsOrigin)
    : location.pathname;
  const [expandedRoute, setExpandedRoute] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isMobileOpen) return;

    const focusTimer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 220);

    return () => window.clearTimeout(focusTimer);
  }, [isMobileOpen]);

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearchSubmit?.();
  };

  const renderItem = (item: AppSidebarItem) => {
    const hasChildren = Boolean(item.children?.length);
    const isActive =
      activePath === item.route ||
      (hasChildren && activePath.startsWith(`${item.route}/`)) ||
      (item.route === "/app/conteudo" && activePath === "/app");
    const isExpanded =
      expandedRoute === item.route && (isOpen || isMobileOpen);
    const submenuId = `submenu-${item.route.replace(/\//g, "-")}`;
    const groupDestination =
      item.children?.find((child) => child.route === "/app")?.route ??
      item.route;

    if (!hasChildren) {
      return (
        <NavLink
          key={item.route}
          to={item.route}
          end={item.end}
          aria-label={item.label}
          title={isOpen ? undefined : item.label}
          onClick={onNavigate}
          className={({ isActive: isLinkActive }) =>
            `app-sidebar__item${
              isLinkActive || isActive ? " app-sidebar__item--active" : ""
            }${item.mobileOnly ? " app-sidebar__item--mobile-only" : ""}`
          }
        >
          <span className="app-sidebar__icon" aria-hidden="true">
            {item.icon}
          </span>
          <span className="app-sidebar__label">{item.label}</span>
        </NavLink>
      );
    }

    return (
      <div
        key={item.route}
        className={`app-sidebar__group${
          isActive ? " app-sidebar__group--active" : ""
        }${isExpanded ? " app-sidebar__group--expanded" : ""}${
          item.mobileOnly ? " app-sidebar__group--mobile-only" : ""
        }`}
      >
        <div className="app-sidebar__group-header">
          <NavLink
            to={groupDestination}
            end={groupDestination === "/app"}
            className={`app-sidebar__item app-sidebar__group-link${
              isActive ? " app-sidebar__item--active" : ""
            }`}
            aria-label={`Ir para ${item.label}`}
            title={isOpen ? undefined : item.label}
            onClick={onNavigate}
          >
            <span className="app-sidebar__icon" aria-hidden="true">
              {item.icon}
            </span>
            <span className="app-sidebar__label">{item.label}</span>
          </NavLink>
          <button
            type="button"
            className="app-sidebar__submenu-chevron"
            onClick={() => {
              if (!isOpen && !isMobileOpen) {
                setExpandedRoute(item.route);
                onRequestOpen?.();
                return;
              }

              setExpandedRoute((currentRoute) =>
                currentRoute === item.route ? null : item.route
              );
            }}
            aria-label={`${isExpanded ? "Fechar" : "Abrir"} submenu de ${
              item.label
            }`}
            aria-expanded={isExpanded}
            aria-controls={submenuId}
            title={`${isExpanded ? "Fechar" : "Abrir"} submenu de ${
              item.label
            }`}
          >
            <ExpandMoreRoundedIcon aria-hidden="true" />
          </button>
        </div>

        <div
          id={submenuId}
          className="app-sidebar__submenu"
          aria-hidden={!isExpanded}
        >
          <div className="app-sidebar__submenu-content">
            {item.children?.map((child) =>
              child.disabled ? (
                <span
                  key={child.route}
                  className="app-sidebar__submenu-item app-sidebar__submenu-item--disabled"
                  aria-disabled="true"
                >
                  <span
                    className="app-sidebar__submenu-arrow"
                    aria-hidden="true"
                  />
                  <span>{child.label}</span>
                </span>
              ) : child.action === "logout" ? (
                <button
                  key={child.route}
                  type="button"
                  tabIndex={isExpanded ? 0 : -1}
                  className="app-sidebar__submenu-item app-sidebar__submenu-item--danger"
                  onClick={() => {
                    onNavigate?.();
                    onLogout?.();
                  }}
                >
                  <span
                    className="app-sidebar__submenu-arrow"
                    aria-hidden="true"
                  />
                  <span>{child.label}</span>
                </button>
              ) : (
                <NavLink
                  key={child.route}
                  to={child.route}
                  end={child.route === "/app"}
                  tabIndex={isExpanded ? 0 : -1}
                  onClick={onNavigate}
                  className={({ isActive: isChildActive }) =>
                    `app-sidebar__submenu-item${
                      isChildActive ||
                      activePath === child.route ||
                      (child.route !== "/app" &&
                        activePath.startsWith(`${child.route}/`))
                        ? " app-sidebar__submenu-item--active"
                        : ""
                    }`
                  }
                >
                  <span
                    className="app-sidebar__submenu-arrow"
                    aria-hidden="true"
                  />
                  <span>{child.label}</span>
                </NavLink>
              )
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        className={`app-sidebar-backdrop${
          isMobileOpen ? " app-sidebar-backdrop--visible" : ""
        }`}
        onClick={onMobileClose}
        aria-label="Fechar menu"
        tabIndex={isMobileOpen ? 0 : -1}
      />

      <aside
        id="app-sidebar"
        className={`app-sidebar${
          isOpen ? "" : " app-sidebar--collapsed"
        }${isMobileOpen ? " app-sidebar--mobile-open" : ""}`}
        aria-label="Menu principal"
      >
        <div className="app-sidebar__mobile-heading">
          <NavLink
            to="/app"
            className="app-sidebar__mobile-brand"
            aria-label="Ir para a tela inicial"
            onClick={onNavigate}
          >
            <img src={logo} alt="Conectado" />
          </NavLink>
          <button
            type="button"
            className="app-sidebar__mobile-close"
            onClick={onMobileClose}
            aria-label="Fechar menu"
          >
            <CloseRoundedIcon aria-hidden="true" />
          </button>
        </div>

        <NavLink
          to="/app"
          className="app-sidebar__brand"
          aria-label="Ir para a tela inicial"
          title="Tela inicial"
          onClick={onNavigate}
        >
          <img src={logo} alt="Conectado" className="app-sidebar__logo" />
        </NavLink>

        <form
          className="app-sidebar__mobile-search"
          role="search"
          onSubmit={handleSearchSubmit}
        >
          <SearchRoundedIcon aria-hidden="true" />
          <input
            ref={searchInputRef}
            type="search"
            value={searchValue}
            onChange={(event) => onSearchChange?.(event.target.value)}
            placeholder="Pesquise e pressione Enter"
            aria-label="Pesquisar no menu"
          />
          <button type="submit">OK</button>
        </form>

        <div className="app-sidebar__mobile-tools">
          {showAdminAccess && (
            <button
              type="button"
              className="app-header__admin-button app-sidebar__mobile-admin"
              onClick={onAdminAccessClick}
            >
              <AdminPanelSettingsOutlinedIcon aria-hidden="true" />
              <span>Painel ADM</span>
            </button>
          )}

          <button
            type="button"
            className={`app-header__theme-toggle app-sidebar__mobile-theme${
              isLightMode ? "" : " app-header__theme-toggle--dark"
            }`}
            onClick={onThemeToggle}
            aria-label={isLightMode ? "Modo escuro" : "Modo claro"}
            aria-pressed={!isLightMode}
          >
            <span>{isLightMode ? "LIGHT MODE" : "DARK MODE"}</span>
            <span className="app-header__theme-icon" aria-hidden="true">
              {isLightMode ? (
                <LightModeOutlinedIcon />
              ) : (
                <DarkModeOutlinedIcon />
              )}
            </span>
          </button>
        </div>

        <nav className="app-sidebar__navigation">
          {items.map(renderItem)}
        </nav>
      </aside>
    </>
  );
};

export default AppSidebar;
